import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { MODAL_CONTENT, MODAL_OVERLAY } from "../lib/animations";

const RANKS = ["HEAD ADMIN", "SENIOR ADMIN", "ADMIN", "SENIOR MOD", "MOD", "JRMOD"];
const ACTIVITY_OPTIONS = ["PASSED", "FAILED", "EXEMPT", "."];
const LOA_OPTIONS = ["Not On LOA", "On LOA", "Exempted"];
const STRIKE_OPTIONS = ["None", "Strike 1", "Strike 2", "Strike 3"];
const WARNING_OPTIONS = ["None", "Warning 1", "Warning 2"];
const SUSPENDED_OPTIONS = ["Not Suspended", "Suspended"];
const QUOTA_MINUTES = 150;

const parseTimeToMinutes = (str) => {
  if (!str || !str.trim()) return 0;
  str = str.trim().toLowerCase();
  let hours = 0, minutes = 0;
  if (str.includes(":")) {
    const parts = str.split(":");
    hours = parseInt(parts[0]) || 0;
    minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  }
  const decimalMatch = str.match(/^(\d+\.?\d*)\s*(h|hr|hrs|hour|hours)?$/);
  if (decimalMatch && decimalMatch[2]) return Math.round(parseFloat(decimalMatch[1]) * 60);
  const hourMatch = str.match(/(\d+)\s*(h|hr|hrs|hour|hours)/);
  const minMatch = str.match(/(\d+)\s*(m|min|mins|minute|minutes)/);
  if (hourMatch) hours = parseInt(hourMatch[1]);
  if (minMatch) minutes = parseInt(minMatch[1]);
  if (!hourMatch && !minMatch && /^\d+$/.test(str)) return parseInt(str);
  return hours * 60 + minutes;
};

const formatMinutesToDisplay = (totalMinutes) => {
  if (totalMinutes <= 0) return "0m";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const StaffModal = ({ isOpen, onClose, staff, onSave, isEditing }) => {
  const [formData, setFormData] = useState({
    name: "", username: "", user_id: "", rank: "JRMOD",
    general_activity: ".", total_time: "", reports: ".",
    warning_status: "None", strike_status: "None",
    loa_status: "Not On LOA", suspended: "Not Suspended", notes: "None", loa_end: "", role_title: "",
  });
  const [timeEntries, setTimeEntries] = useState("");
  const [calculatedTime, setCalculatedTime] = useState(0);

  useEffect(() => {
    if (staff && isEditing) {
      setFormData({ ...staff, suspended: staff.suspended || "Not Suspended" });
      setTimeEntries(staff.total_time || "");
    } else {
      setFormData({
        name: "", username: "", user_id: "", rank: "JRMOD",
        general_activity: ".", total_time: "", reports: ".",
        warning_status: "None", strike_status: "None",
        loa_status: "Not On LOA", suspended: "Not Suspended", notes: "None", loa_end: "", role_title: "",
      });
      setTimeEntries("");
    }
  }, [staff, isEditing, isOpen]);

  useEffect(() => {
    const lines = timeEntries.split(/[\n,+]/).filter((line) => line.trim());
    let totalMinutes = 0;
    lines.forEach((line) => { totalMinutes += parseTimeToMinutes(line); });
    setCalculatedTime(totalMinutes);
    const formatted = totalMinutes > 0 ? formatMinutesToDisplay(totalMinutes) : "";
    setFormData((prev) => {
      const updated = { ...prev, total_time: formatted };
      if (totalMinutes >= QUOTA_MINUTES && prev.general_activity?.toUpperCase() !== "EXEMPT") {
        updated.general_activity = "PASSED";
      }
      return updated;
    });
  }, [timeEntries]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Exclude game_reports — only the bot should update this field
    const { game_reports, ...submitData } = formData;
    onSave(submitData);
  };
  const addQuickTime = (time) => { setTimeEntries((prev) => (prev ? `${prev}\n${time}` : time)); };

  if (!isOpen) return null;

  const remaining = QUOTA_MINUTES - calculatedTime;
  const progress = Math.min((calculatedTime / QUOTA_MINUTES) * 100, 100);
  const quotaMet = calculatedTime >= QUOTA_MINUTES;

  return (
    <AnimatePresence>
      <motion.div
        {...MODAL_OVERLAY}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="staff-modal-overlay"
      >
        <motion.div
          {...MODAL_CONTENT}
          className="glass-overlay w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="staff-modal"
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent" />
          <div className="p-5 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <h2 className="font-mono text-sm font-bold text-zinc-200 uppercase tracking-wider">
                {isEditing ? "Edit Staff Member" : "Add New Staff"}
              </h2>
              <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 transition-colors" data-testid="close-modal-btn">
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Name *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-field w-full" data-testid="staff-name-input" />
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Username *</label>
                <input type="text" required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="input-field w-full" data-testid="staff-username-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Discord ID</label>
                <input type="text" required value={formData.user_id} onChange={(e) => setFormData({ ...formData, user_id: e.target.value })} className="input-field w-full" placeholder="Right-click user > Copy ID" data-testid="staff-userid-input" />
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Rank *</label>
                <select value={formData.rank} onChange={(e) => setFormData({ ...formData, rank: e.target.value })} className="select-field w-full" data-testid="staff-rank-select">
                  {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Activity</label>
                <select value={formData.general_activity} onChange={(e) => setFormData({ ...formData, general_activity: e.target.value })} className="select-field w-full" data-testid="staff-activity-select">
                  {ACTIVITY_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Reports</label>
                <input type="text" value={formData.reports} onChange={(e) => setFormData({ ...formData, reports: e.target.value })} className="input-field w-full" data-testid="staff-reports-input" />
              </div>
            </div>

            {/* Time Tracker */}
            <div className={`border p-4 ${quotaMet ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-zinc-900/50"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className={`w-4 h-4 ${quotaMet ? "text-emerald-400" : "text-[#00E5FF]"}`} />
                  <span className="font-mono text-xs text-zinc-300 font-semibold">TIME TRACKER</span>
                  <span className="font-mono text-[9px] text-zinc-600">(2h 30m quota)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-lg font-bold ${quotaMet ? "text-emerald-400" : "text-zinc-200"}`}>
                    {formatMinutesToDisplay(calculatedTime)}
                  </span>
                  <div className={`px-2 py-0.5 text-[10px] font-mono font-bold border ${
                    quotaMet ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : remaining <= 30 ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/30"
                  }`}>
                    {quotaMet ? (
                      <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />+{formatMinutesToDisplay(Math.abs(remaining))}</span>
                    ) : (
                      <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{formatMinutesToDisplay(remaining)} LEFT</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="h-2 bg-zinc-800 overflow-hidden mb-3">
                <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }}
                  className={`h-full ${quotaMet ? "bg-emerald-500" : progress >= 70 ? "bg-amber-500" : "bg-[#FF3366]"}`} />
              </div>

              <div>
                <label className="block font-mono text-[9px] text-zinc-600 mb-1.5">Enter times (one per line, comma, or +)</label>
                <textarea value={timeEntries} onChange={(e) => setTimeEntries(e.target.value)} placeholder="1h 30m&#10;45m&#10;22m" className="input-field w-full h-16 resize-none text-xs" data-testid="staff-time-input" />
              </div>

              <div className="flex flex-wrap gap-1.5 mt-2">
                {["15m", "30m", "45m", "1h", "1h 30m", "2h"].map((time) => (
                  <button key={time} type="button" onClick={() => addQuickTime(time)}
                    className="px-2 py-0.5 text-[10px] font-mono bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-400 hover:text-[#00E5FF] transition-colors">
                    +{time}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Warning</label>
                <select value={formData.warning_status} onChange={(e) => setFormData({ ...formData, warning_status: e.target.value })} className="select-field w-full" data-testid="staff-warning-select">
                  {WARNING_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Strike</label>
                <select value={formData.strike_status} onChange={(e) => setFormData({ ...formData, strike_status: e.target.value })} className="select-field w-full" data-testid="staff-strike-select">
                  {STRIKE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">LOA</label>
                <select value={formData.loa_status} onChange={(e) => setFormData({ ...formData, loa_status: e.target.value })} className="select-field w-full" data-testid="staff-loa-select">
                  {LOA_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Suspended</label>
                <select value={formData.suspended} onChange={(e) => setFormData({ ...formData, suspended: e.target.value })} className="select-field w-full" data-testid="staff-suspended-select">
                  {SUSPENDED_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Notes</label>
                <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-field w-full" data-testid="staff-notes-input" />
              </div>
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">LOA End Date</label>
                <input type="text" value={formData.loa_end} onChange={(e) => setFormData({ ...formData, loa_end: e.target.value })} className="input-field w-full" placeholder="e.g., 14/02/26" data-testid="staff-loaend-input" />
              </div>
            </div>

            <div>
              <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Role Title</label>
              <input type="text" value={formData.role_title} onChange={(e) => setFormData({ ...formData, role_title: e.target.value })} className="input-field w-full" placeholder="e.g., Head of Staff Operations" data-testid="staff-roletitle-input" />
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-btn">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-staff-btn">{isEditing ? "UPDATE" : "ADD STAFF"}</button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StaffModal;
